import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, MessageCircle, Loader2, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const { t, i18n } = useTranslation();
  const [contactInfo, setContactInfo] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('idle'); // idle | loading | pending | already | error
  const [newsletterError, setNewsletterError] = useState('');

  const submitNewsletter = async (e) => {
    e.preventDefault();
    const email = newsletterEmail.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setNewsletterStatus('error');
      setNewsletterError(t('footer.newsletter_invalid', 'Zadajte platný email'));
      return;
    }
    setNewsletterStatus('loading');
    setNewsletterError('');
    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          language: (i18n.language || 'sk').split('-')[0].toLowerCase(),
          source: 'footer',
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setNewsletterStatus('error');
        setNewsletterError(data.detail || t('footer.newsletter_error', 'Skúste to znova neskôr'));
        return;
      }
      const data = await res.json();
      setNewsletterStatus(data.status === 'already_confirmed' ? 'already' : 'pending');
      setNewsletterEmail('');
    } catch (_err) {
      setNewsletterStatus('error');
      setNewsletterError(t('footer.newsletter_error', 'Skúste to znova neskôr'));
    }
  };

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/contact`);
      if (response.ok) {
        const data = await response.json();
        setContactInfo(data);
      }
    } catch (error) {
      console.error('Error fetching contact info:', error);
    }
  };

  // Default values
  const info = contactInfo || {
    email: 'info@sunify.sk',
    phone: '+421 233 070 865',
    whatsapp: '+421 952 411 679',
    address: { city: 'Bratislava', country: 'Slovakia' },
    social: { facebook: 'https://facebook.com/sunify', instagram: 'https://instagram.com/sunify' }
  };

  const whatsappNumber = info.whatsapp?.replace(/[^0-9]/g, '') || '421952411679';

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* About */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-orange-400">{t('footer.about')}</h3>
            <img 
              src="https://customer-assets.emergentagent.com/job_welcome-app-440/artifacts/1g8bb7ag_Sunify%20logo%20ok..jpg" 
              alt="Sunify" 
              className="h-16 w-auto mb-4"
            />
            <p className="text-gray-400 text-sm mb-4">
              Sunify - profesionálne slnečné fólie pre domácnosti aj firmy.
            </p>
            {/* Social Media */}
            <div className="flex space-x-3">
              <a 
                href="https://www.facebook.com/1338460386016385" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition"
                data-testid="social-facebook"
                aria-label="Facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"/>
                </svg>
              </a>
              <a 
                href="https://www.instagram.com/sunify.sk/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-pink-600 transition"
                data-testid="social-instagram"
                aria-label="Instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a 
                href={`https://wa.me/${whatsappNumber}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-green-600 transition"
                data-testid="social-whatsapp"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-orange-400">{t('footer.quick_links')}</h3>
            <ul className="space-y-2">
              <li><Link to="/catalog" className="text-gray-400 hover:text-orange-400 transition">{t('nav.catalog')}</Link></li>
              <li><Link to="/brands" className="text-gray-400 hover:text-orange-400 transition">{t('nav.brands')}</Link></li>
              <li><Link to="/services" className="text-gray-400 hover:text-orange-400 transition">{t('nav.services')}</Link></li>
              <li><Link to="/blog" className="text-gray-400 hover:text-orange-400 transition">{t('nav.blog')}</Link></li>
              <li><Link to="/gallery" className="text-gray-400 hover:text-orange-400 transition">{t('nav.gallery')}</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-orange-400">{t('footer.contact_us')}</h3>
            <ul className="space-y-3 text-gray-400 text-sm">
              <li className="flex items-center space-x-2">
                <Mail className="h-4 w-4" />
                <a href={`mailto:${info.email}`} className="hover:text-orange-400">{info.email}</a>
              </li>
              <li className="flex items-center space-x-2">
                <Phone className="h-4 w-4" />
                <a href={`tel:${info.phone?.replace(/\s/g, '')}`} className="hover:text-orange-400">{info.phone}</a>
              </li>
              <li className="flex items-center space-x-2">
                <MessageCircle className="h-4 w-4 text-green-500" />
                <a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="hover:text-green-400">
                  WhatsApp: {info.whatsapp}
                </a>
              </li>
              <li className="flex items-center space-x-2">
                <MapPin className="h-4 w-4" />
                <span>{info.address?.city}, {info.address?.country}</span>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-orange-400">{t('footer.newsletter')}</h3>
            <p className="text-gray-400 text-sm mb-4">
              {t('footer.newsletter_intro', 'Prihláste sa na odber noviniek a špeciálnych ponúk.')}
            </p>
            <form onSubmit={submitNewsletter} className="flex" data-testid="footer-newsletter-form">
              <input
                type="email"
                required
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                disabled={newsletterStatus === 'loading'}
                placeholder={t('footer.newsletter_placeholder', 'Váš email')}
                className="flex-1 min-w-0 px-4 py-2 rounded-l-md bg-gray-800 text-white border border-gray-700 focus:outline-none focus:border-orange-400"
                data-testid="footer-newsletter-email"
              />
              <button
                type="submit"
                disabled={newsletterStatus === 'loading'}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-r-md transition disabled:opacity-60 flex items-center justify-center"
                data-testid="footer-newsletter-submit"
              >
                {newsletterStatus === 'loading'
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : t('footer.subscribe')}
              </button>
            </form>
            <div className="mt-2 text-xs min-h-[1.25rem]" data-testid="footer-newsletter-feedback">
              {newsletterStatus === 'pending' && (
                <span className="text-green-400 inline-flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {t('footer.newsletter_pending', 'Skontrolujte email a potvrďte odber.')}
                </span>
              )}
              {newsletterStatus === 'already' && (
                <span className="text-green-400">{t('footer.newsletter_already', 'Už ste prihlásený, ďakujeme!')}</span>
              )}
              {newsletterStatus === 'error' && (
                <span className="text-red-400">{newsletterError}</span>
              )}
            </div>
            <p className="mt-2 text-[11px] text-gray-500 leading-snug">
              {t('footer.newsletter_gdpr', 'Odoslaním súhlasíte so spracovaním e-mailu na marketingové účely. Odhlásenie kedykoľvek jedným klikom.')}
            </p>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-sm text-gray-400">
          <div className="flex flex-wrap gap-x-5 gap-y-2 justify-center md:justify-start" data-testid="footer-legal-links">
            <Link to="/legal/returns" className="hover:text-orange-400 transition">
              {t('footer.legal_returns', 'Vrátenie tovaru')}
            </Link>
            <Link to="/legal/shipping" className="hover:text-orange-400 transition">
              {t('footer.legal_shipping', 'Doprava')}
            </Link>
            <Link to="/legal/privacy" className="hover:text-orange-400 transition">
              {t('footer.legal_privacy', 'Ochrana údajov')}
            </Link>
            <Link to="/legal/terms" className="hover:text-orange-400 transition">
              {t('footer.legal_terms', 'Obchodné podmienky')}
            </Link>
            <button
              type="button"
              onClick={() => {
                try {
                  localStorage.removeItem('sunify_consent_v1');
                  window.location.reload();
                } catch (e) { /* ignore */ }
              }}
              className="hover:text-orange-400 transition cursor-pointer"
              data-testid="footer-cookie-settings"
            >
              {t('footer.cookie_settings', 'Cookies')}
            </button>
          </div>
          <div className="text-center md:text-right">
            <p>&copy; {new Date().getFullYear()} Sunify. {t('footer.rights')}</p>
            <p><a href="https://www.hhgroup.sk/" className="hover:text-orange-400 transition">HH Group</a> · <a href="https://www.facebook.com/1338460386016385" target="_blank" rel="noopener noreferrer" className="hover:text-orange-400 transition">Facebook</a> · <a href="https://www.instagram.com/sunify.sk/" target="_blank" rel="noopener noreferrer" className="hover:text-orange-400 transition">Instagram</a></p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
